import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { formatDate, isOverdue, copyToClipboard, calculateVATInclusiveAmount, sendEmail } from "../../lib/appUtils";
import { GlobalNav } from "../shared/GlobalNav";
import { AccountingIntegrationsPanel } from "../shared/AccountingIntegrationsPanel";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  FileText, Plus, Eye, Edit3, Send, Trash2, CheckCircle, Copy,
  ArrowRight, AlertTriangle, Clock, Calendar, CreditCard, 
  Users, ExternalLink, Upload, Mail, AlertCircle, RefreshCw, Banknote,
  PiggyBank, Zap, Settings, Shield
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [showUploadInvoice, setShowUploadInvoice] = useState(false);
  const [savingInvoice, setSavingInvoice] = useState(false);
  // Project linking mode for the Create Invoice flow:
  //   'new'      → spin up a brand-new project from the title field
  //   'existing' → attach this invoice to an already-existing project
  const [projectLinkMode, setProjectLinkMode] = useState('new');
  const [newInvoice, setNewInvoice] = useState({
    client_name: '',
    client_email: '',
    project_title: '',
    existing_project_id: '',
    issue_date: new Date().toISOString().slice(0, 10),
    vat_enabled: true,
    vat_rate: 20,
    invoice_due_days: 14,
    line_items: [{ description: '', quantity: 1, rate: 0, vat_rate: 20 }],
  });
  const [uploadForm, setUploadForm] = useState({ project_id: '', file: null, fileName: '' });
  const { token, user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch invoices",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewInvoice = async (projectId) => {
    setPreviewLoading(true);
    setPreviewInvoice({ project_id: projectId, invoice_content: null });
    try {
      const response = await axios.get(`${API}/projects/${projectId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewInvoice(response.data);
    } catch (error) {
      toast({
        title: "Preview failed",
        description: error.response?.data?.detail || "Could not load the invoice preview.",
        variant: "destructive"
      });
      setPreviewInvoice(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadInvoice = () => {
    if (!previewInvoice?.invoice_content) return;
    const blob = new Blob([previewInvoice.invoice_content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${previewInvoice.invoice_number || 'invoice'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePrintInvoice = () => {
    if (!previewInvoice?.invoice_content) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(previewInvoice.invoice_content);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (_) {} }, 400);
  };

  // ---- New Invoice (inline) ----------------------------------------------
  const fetchAllProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects?include_archived=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllProjects(response.data || []);
    } catch (_) { /* non-fatal */ }
  };

  useEffect(() => { fetchAllProjects(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNewInvoice = () => {
    setProjectLinkMode('new');
    setNewInvoice({
      client_name: '',
      client_email: '',
      project_title: '',
      existing_project_id: '',
      issue_date: new Date().toISOString().slice(0, 10),
      vat_enabled: true,
      vat_rate: 20,
      invoice_due_days: 14,
      line_items: [{ description: '', quantity: 1, rate: 0, vat_rate: 20 }],
    });
    setShowNewInvoice(true);
  };

  const updateLineItem = (idx, field, value) => {
    setNewInvoice(prev => {
      const lines = [...prev.line_items];
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...prev, line_items: lines };
    });
  };

  const addLineItem = () => {
    setNewInvoice(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, rate: 0, vat_rate: prev.vat_rate }],
    }));
  };

  const removeLineItem = (idx) => {
    setNewInvoice(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== idx),
    }));
  };

  const invoiceTotals = (() => {
    const subtotal = newInvoice.line_items.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.rate) || 0), 0);
    const vat = newInvoice.vat_enabled
      ? newInvoice.line_items.reduce((s, l) => s + ((Number(l.quantity) || 0) * (Number(l.rate) || 0)) * ((Number(l.vat_rate) || 0) / 100), 0)
      : 0;
    return { subtotal: +subtotal.toFixed(2), vat: +vat.toFixed(2), total: +(subtotal + vat).toFixed(2) };
  })();

  const handleSubmitNewInvoice = async () => {
    // Validate per linking mode
    if (projectLinkMode === 'existing') {
      if (!newInvoice.existing_project_id) {
        toast({ title: "Pick a project", description: "Choose which project this invoice belongs to.", variant: "destructive" });
        return;
      }
    } else {
      if (!newInvoice.client_name.trim() || !newInvoice.client_email.trim() || !newInvoice.project_title.trim()) {
        toast({ title: "Missing fields", description: "Please fill in client name, email and project title.", variant: "destructive" });
        return;
      }
    }
    if (invoiceTotals.subtotal <= 0) {
      toast({ title: "No line items", description: "Add at least one line item with a rate above £0.", variant: "destructive" });
      return;
    }
    setSavingInvoice(true);
    try {
      if (projectLinkMode === 'existing') {
        // Pull the existing project so we don't blank its client/contact fields
        // when the backend PUT replaces those keys.
        const existing = allProjects.find(p => p.id === newInvoice.existing_project_id) || {};
        await axios.put(
          `${API}/projects/${newInvoice.existing_project_id}/invoice`,
          {
            line_items: newInvoice.line_items.map(l => ({
              description: l.description,
              quantity: Number(l.quantity) || 0,
              rate: Number(l.rate) || 0,
              vat_rate: Number(l.vat_rate) || 0,
            })),
            invoice_due_days: Number(newInvoice.invoice_due_days) || 14,
            issue_date: newInvoice.issue_date,
            default_vat_rate: Number(newInvoice.vat_rate) || 20,
            // Preserve existing client info — don't blank these on update.
            client_name: existing.client_name || '',
            client_email: existing.client_email || '',
            client_contact: existing.client_contact || '',
            client_phone: existing.client_phone || '',
            client_address: existing.client_address || '',
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: "Invoice attached", description: `Filed under ${existing.title || 'project'}.` });
      } else {
        const payload = {
          title: newInvoice.project_title.trim(),
          client_name: newInvoice.client_name.trim(),
          client_email: newInvoice.client_email.trim().toLowerCase(),
          total_amount: invoiceTotals.subtotal,
          currency: 'GBP',
          vat_enabled: newInvoice.vat_enabled,
          vat_rate: Number(newInvoice.vat_rate) || 20,
          invoice_due_days: Number(newInvoice.invoice_due_days) || 14,
          payment_structure: 'invoice',
          is_invoice_project: true,
          provider: 'test',
          line_items: newInvoice.line_items.map(l => ({
            description: l.description,
            quantity: Number(l.quantity) || 0,
            rate: Number(l.rate) || 0,
            vat_rate: Number(l.vat_rate) || 0,
          })),
        };
        await axios.post(`${API}/projects`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: "Invoice created", description: `${payload.title} is live in the Invoices hub.` });
      }
      setShowNewInvoice(false);
      fetchInvoices();
      fetchAllProjects();
    } catch (err) {
      toast({
        title: "Could not create invoice",
        description: err.response?.data?.detail || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingInvoice(false);
    }
  };

  // ---- Upload existing invoice -------------------------------------------
  const openUploadInvoice = () => {
    setUploadForm({ project_id: '', file: null, fileName: '' });
    setShowUploadInvoice(true);
  };

  const handleUploadInvoice = async () => {
    if (!uploadForm.project_id) {
      toast({ title: "Pick a project", description: "Select which project this invoice belongs to.", variant: "destructive" });
      return;
    }
    if (!uploadForm.file) {
      toast({ title: "No file", description: "Choose a PDF or image to upload.", variant: "destructive" });
      return;
    }
    setSavingInvoice(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      await axios.post(
        `${API}/projects/${uploadForm.project_id}/invoice/upload`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast({ title: "Invoice uploaded", description: `${uploadForm.fileName} is now attached to the project.` });
      setShowUploadInvoice(false);
      fetchInvoices();
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err.response?.data?.detail || "Could not upload the file.",
        variant: "destructive",
      });
    } finally {
      setSavingInvoice(false);
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (filter === 'pending') return invoice.status === 'pending';
    if (filter === 'paid') return invoice.status === 'paid';
    if (filter === 'overdue') return invoice.status === 'overdue';
    return true; // 'all'
  });

  const getInvoiceStats = () => {
    return {
      total: invoices.length,
      pending: invoices.filter(inv => inv.status === 'pending').length,
      paid: invoices.filter(inv => inv.status === 'paid').length,
      overdue: invoices.filter(inv => inv.status === 'overdue').length,
      totalAmount: invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0)
    };
  };

  const stats = getInvoiceStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} currentPage="invoices" />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
            <p className="mt-4 text-gray-600 font-body">Loading invoices...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="invoices" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Invoices</h1>
              <p className="text-gray-600 font-body">All your invoice projects in one place — preview, send, mark paid.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={openUploadInvoice}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="invoices-upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Invoice
              </Button>
              <Button
                onClick={openNewInvoice}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="invoices-new-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Create Invoice
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Overview — slick coloured-tile aesthetic matching the per-project Invoice tab */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide font-body">Total</p>
              <FileText className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{stats.total}</p>
            <p className="text-xs text-gray-500 mt-1 font-body">Invoices</p>
          </div>

          <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-yellow-100 border border-amber-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide font-body">Pending</p>
              <Clock className="w-4 h-4 text-amber-500" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-amber-900 font-heading">{stats.pending}</p>
            <p className="text-xs text-amber-700/70 mt-1 font-body">Awaiting payment</p>
          </div>

          <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-green-100 border border-emerald-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide font-body">Paid</p>
              <CheckCircle className="w-4 h-4 text-emerald-500" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-emerald-900 font-heading">{stats.paid}</p>
            <p className="text-xs text-emerald-700/70 mt-1 font-body">Settled</p>
          </div>

          <div className="rounded-2xl bg-gradient-to-br from-rose-50 to-red-100 border border-rose-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-rose-700 uppercase tracking-wide font-body">Overdue</p>
              <AlertTriangle className="w-4 h-4 text-rose-500" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-rose-900 font-heading">{stats.overdue}</p>
            <p className="text-xs text-rose-700/70 mt-1 font-body">Needs chasing</p>
          </div>

          <div className="rounded-2xl bg-gradient-to-br from-pink-50 to-fuchsia-100 border border-pink-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-[#e6007e] uppercase tracking-wide font-body">Total Value</p>
              <Banknote className="w-4 h-4 text-[#e6007e]" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-[#e6007e] font-heading">£{stats.totalAmount.toLocaleString()}</p>
            <p className="text-xs text-pink-700/70 mt-1 font-body">All-time billed</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center space-x-4 mb-6">
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium rounded-l-md border font-body ${
                filter === 'all' 
                  ? 'bg-[#e6007e] text-white border-[#e6007e]' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              All Invoices
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 text-sm font-medium border-t border-b font-body ${
                filter === 'pending' 
                  ? 'bg-[#e6007e] text-white border-[#e6007e]' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter('paid')}
              className={`px-4 py-2 text-sm font-medium border-t border-b font-body ${
                filter === 'paid' 
                  ? 'bg-[#e6007e] text-white border-[#e6007e]' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Paid
            </button>
            <button
              onClick={() => setFilter('overdue')}
              className={`px-4 py-2 text-sm font-medium rounded-r-md border font-body ${
                filter === 'overdue' 
                  ? 'bg-[#e6007e] text-white border-[#e6007e]' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Overdue
            </button>
          </div>
        </div>

        {/* Invoices List — slick card with icon header */}
        <Card className="border-0 shadow-sm bg-white rounded-2xl">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 font-heading">
                {filter === 'all' ? 'All Invoices' : filter === 'pending' ? 'Pending Invoices' : filter === 'paid' ? 'Paid Invoices' : 'Overdue Invoices'}
              </h2>
              <span className="ml-auto text-xs text-gray-500 font-body">{filteredInvoices.length} {filteredInvoices.length === 1 ? 'invoice' : 'invoices'}</span>
            </div>

            {filteredInvoices.length === 0 ? (
              <div className="text-center py-16 px-6 rounded-xl bg-gray-50/60 border border-dashed border-gray-200">
                <div className="w-16 h-16 rounded-2xl bg-white border border-gray-200 shadow-sm mx-auto mb-4 flex items-center justify-center">
                  <FileText className="w-7 h-7 text-gray-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1 font-heading">
                  {filter === 'all' ? 'No invoices yet' : `No ${filter} invoices`}
                </h3>
                <p className="text-sm text-gray-500 font-body max-w-sm mx-auto">
                  Create an invoice project to start tracking invoices and payments here.
                </p>
                <Button
                  onClick={openNewInvoice}
                  className="mt-5 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredInvoices.map((invoice) => (
                  <div
                    key={invoice.project_id}
                    className="group relative rounded-xl border border-gray-200 bg-white p-5 hover:border-[#e6007e]/40 hover:shadow-md transition-all"
                    data-testid={`invoice-row-${invoice.invoice_number}`}
                  >
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                          <h3 className="text-base md:text-lg font-bold text-gray-900 font-heading">{invoice.title}</h3>
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide ${
                              invoice.status === 'paid'
                                ? 'bg-emerald-100 text-emerald-700'
                                : invoice.status === 'overdue'
                                ? 'bg-rose-100 text-rose-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {invoice.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-x-5 gap-y-1 text-xs text-gray-500 flex-wrap font-body">
                          <span><span className="text-gray-400">#</span> {invoice.invoice_number}</span>
                          <span><span className="text-gray-400">Due</span> {formatDate(invoice.due_date)}</span>
                          {invoice.client_name && <span><span className="text-gray-400">Client</span> {invoice.client_name}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-lg md:text-xl font-bold text-gray-900 font-heading">£{(invoice.total_amount || 0).toLocaleString()}</p>
                          <p className="text-[11px] text-gray-500 font-body">total</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreviewInvoice(invoice.project_id)}
                          className="font-body"
                          data-testid={`invoice-preview-btn-${invoice.invoice_number}`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Preview
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/projects/${invoice.project_id}?tab=invoice`, '_blank')}
                          className="font-body"
                        >
                          <Edit3 className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/projects/${invoice.project_id}`, '_blank')}
                          className="font-body text-gray-500 hover:text-[#e6007e]"
                          title="Open project"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Accounting Integrations — promoted globally via shared component */}
        <div className="mt-10">
          <AccountingIntegrationsPanel />
        </div>
      </main>

      {/* New Invoice — inline editor matching the per-project Invoice tab style */}
      <Dialog open={showNewInvoice} onOpenChange={setShowNewInvoice}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" />Create Invoice</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto space-y-5 pr-1">
            <Card className="border-0 shadow-sm bg-white rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 font-heading">Client &amp; project</h3>
                </div>

                {/* Link mode toggle */}
                <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 mb-4">
                  <button
                    type="button"
                    onClick={() => setProjectLinkMode('new')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${projectLinkMode === 'new' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    data-testid="new-invoice-mode-new"
                  >
                    New project
                  </button>
                  <button
                    type="button"
                    onClick={() => setProjectLinkMode('existing')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${projectLinkMode === 'existing' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    data-testid="new-invoice-mode-existing"
                  >
                    Existing project
                  </button>
                </div>

                {projectLinkMode === 'existing' ? (
                  <div>
                    <Label className="text-xs text-gray-600">Pick a project</Label>
                    <select
                      value={newInvoice.existing_project_id}
                      onChange={(e) => setNewInvoice({ ...newInvoice, existing_project_id: e.target.value })}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                      data-testid="new-invoice-existing-project-select"
                    >
                      <option value="">Select a project…</option>
                      {allProjects.map(p => (
                        <option key={p.id} value={p.id}>{p.title} {p.client_name ? `— ${p.client_name}` : ''}</option>
                      ))}
                    </select>
                    <p className="text-[11px] text-gray-500 mt-2">The invoice will be filed under this project. Client details are pulled from the project automatically.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-600">Client name</Label>
                      <Input value={newInvoice.client_name} onChange={(e) => setNewInvoice({ ...newInvoice, client_name: e.target.value })} placeholder="Cat Abrahams" className="mt-1" data-testid="new-invoice-client-name" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Client email</Label>
                      <Input type="email" value={newInvoice.client_email} onChange={(e) => setNewInvoice({ ...newInvoice, client_email: e.target.value })} placeholder="client@example.com" className="mt-1" data-testid="new-invoice-client-email" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs text-gray-600">Project title <span className="text-gray-400">(a new project will be created with this name)</span></Label>
                      <Input value={newInvoice.project_title} onChange={(e) => setNewInvoice({ ...newInvoice, project_title: e.target.value })} placeholder="Brand Identity Refresh" className="mt-1" data-testid="new-invoice-project-title" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Invoice Details — mirrors the per-project Invoice tab layout */}
            <Card className="border-0 shadow-sm bg-white rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-purple-600" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 font-heading">Invoice Details</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-gray-600">Invoice Number</Label>
                    <Input
                      value="Generated on save"
                      disabled
                      className="mt-1 bg-gray-50 text-gray-500"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Auto-assigned (INV-YYYYMMDD-…)</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Issue Date</Label>
                    <Input
                      type="date"
                      value={newInvoice.issue_date}
                      onChange={(e) => setNewInvoice({ ...newInvoice, issue_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Payment Due (Days)</Label>
                    <select
                      value={newInvoice.invoice_due_days}
                      onChange={(e) => setNewInvoice({ ...newInvoice, invoice_due_days: Number(e.target.value) })}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                    >
                      {[7, 14, 21, 30, 45, 60, 90].map(d => (
                        <option key={d} value={d}>{d} days</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-500 font-body">Status:</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-semibold bg-amber-100 text-amber-700 uppercase tracking-wide">Pending</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm bg-white rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-100 to-yellow-100 flex items-center justify-center">
                      <Edit3 className="w-4 h-4 text-amber-600" />
                    </div>
                    <h3 className="text-base font-bold text-gray-900 font-heading">Invoice Line Items</h3>
                  </div>
                  <Button size="sm" variant="outline" onClick={addLineItem} data-testid="new-invoice-add-line"><Plus className="w-3.5 h-3.5 mr-1.5" />Add Line</Button>
                </div>
                <div className="grid grid-cols-12 gap-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
                  <div className="col-span-4">Description</div>
                  <div className="col-span-1 text-center">Qty</div>
                  <div className="col-span-2 text-right">Rate</div>
                  <div className="col-span-1 text-right">VAT %</div>
                  <div className="col-span-2 text-right">Net</div>
                  <div className="col-span-1 text-right">VAT</div>
                  <div className="col-span-1"></div>
                </div>
                {newInvoice.line_items.map((li, idx) => {
                  const qty = Number(li.quantity) || 0;
                  const rate = Number(li.rate) || 0;
                  const vatPct = Number(li.vat_rate) || 0;
                  const lineNet = qty * rate;
                  const lineVat = newInvoice.vat_enabled ? lineNet * (vatPct / 100) : 0;
                  return (
                    <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-center">
                      <Input className="col-span-4" placeholder="Strategy session" value={li.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)} data-testid={`new-invoice-line-${idx}-desc`} />
                      <Input className="col-span-1 text-center" type="number" min="0" value={li.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)} />
                      <Input className="col-span-2 text-right" type="number" min="0" step="0.01" value={li.rate} onChange={(e) => updateLineItem(idx, 'rate', e.target.value)} />
                      <Input className="col-span-1 text-right" type="number" min="0" max="100" value={li.vat_rate} onChange={(e) => updateLineItem(idx, 'vat_rate', e.target.value)} />
                      <div className="col-span-2 text-right text-sm font-semibold text-gray-700">£{lineNet.toFixed(2)}</div>
                      <div className="col-span-1 text-right text-sm font-semibold text-blue-600">£{lineVat.toFixed(2)}</div>
                      <Button className="col-span-1" variant="ghost" size="sm" onClick={() => removeLineItem(idx)} disabled={newInvoice.line_items.length === 1}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                    </div>
                  );
                })}

                <div className="grid grid-cols-3 gap-3 mt-5">
                  <div className="rounded-xl bg-gray-100 border border-gray-200 p-4 text-right">
                    <p className="text-[11px] font-semibold uppercase text-gray-500 tracking-wide">Subtotal (Net)</p>
                    <p className="text-lg font-bold text-gray-900 mt-1 font-heading">£{invoiceTotals.subtotal.toFixed(2)}</p>
                  </div>
                  <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 text-right">
                    <p className="text-[11px] font-semibold uppercase text-blue-600 tracking-wide">VAT</p>
                    <p className="text-lg font-bold text-blue-700 mt-1 font-heading">£{invoiceTotals.vat.toFixed(2)}</p>
                  </div>
                  <div className="rounded-xl bg-pink-50 border border-pink-200 p-4 text-right">
                    <p className="text-[11px] font-semibold uppercase text-[#e6007e] tracking-wide">Total</p>
                    <p className="text-lg font-bold text-[#e6007e] mt-1 font-heading">£{invoiceTotals.total.toFixed(2)}</p>
                  </div>
                </div>

                {newInvoice.vat_enabled && invoiceTotals.vat > 0 && (
                  <div className="mt-3 rounded-xl bg-purple-50 border border-purple-200 px-4 py-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-purple-700">VAT to set aside (cash)</span>
                    <span className="text-base font-bold text-purple-700 font-heading">£{invoiceTotals.vat.toFixed(2)}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm bg-white rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center">
                    <Settings className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 font-heading">VAT &amp; due date</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={newInvoice.vat_enabled} onCheckedChange={(v) => setNewInvoice({ ...newInvoice, vat_enabled: !!v })} />
                    VAT enabled
                  </label>
                  <div>
                    <Label className="text-xs text-gray-600">VAT rate (%)</Label>
                    <Input type="number" value={newInvoice.vat_rate} onChange={(e) => setNewInvoice({ ...newInvoice, vat_rate: e.target.value })} disabled={!newInvoice.vat_enabled} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Payment due (days)</Label>
                    <Input type="number" value={newInvoice.invoice_due_days} onChange={(e) => setNewInvoice({ ...newInvoice, invoice_due_days: e.target.value })} className="mt-1" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 pt-4 mt-3">
            <Button variant="outline" onClick={() => setShowNewInvoice(false)}>Cancel</Button>
            <Button onClick={handleSubmitNewInvoice} disabled={savingInvoice} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-invoice-save-btn">
              <FileText className="w-4 h-4 mr-2" />
              {savingInvoice ? 'Saving…' : 'Create Invoice'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Invoice — attach existing PDF to a project */}
      <Dialog open={showUploadInvoice} onOpenChange={setShowUploadInvoice}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload an existing invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-gray-600">Attach to project</Label>
              <select
                value={uploadForm.project_id}
                onChange={(e) => setUploadForm({ ...uploadForm, project_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                data-testid="upload-invoice-project-select"
              >
                <option value="">Select a project…</option>
                {allProjects.map(p => (
                  <option key={p.id} value={p.id}>{p.title} {p.client_name ? `— ${p.client_name}` : ''}</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500 mt-1">Don&apos;t have one yet? Use the &quot;+ New Invoice&quot; button instead.</p>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Invoice file (PDF, PNG, JPG)</Label>
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null, fileName: e.target.files?.[0]?.name || '' })}
                className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-pink-50 file:text-[#e6007e] hover:file:bg-pink-100"
                data-testid="upload-invoice-file"
              />
              {uploadForm.fileName && <p className="text-xs text-gray-600 mt-1">{uploadForm.fileName}</p>}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200 mt-3">
            <Button variant="outline" onClick={() => setShowUploadInvoice(false)}>Cancel</Button>
            <Button onClick={handleUploadInvoice} disabled={savingInvoice} className="bg-[#e6007e] hover:bg-[#cc006f] text-white" data-testid="upload-invoice-save-btn">
              {savingInvoice ? 'Uploading…' : 'Upload invoice'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Preview Modal */}
      <Dialog open={!!previewInvoice} onOpenChange={(open) => !open && setPreviewInvoice(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>
                Invoice Preview
                {previewInvoice?.invoice_number && (
                  <span className="ml-2 text-sm text-gray-500 font-normal">#{previewInvoice.invoice_number}</span>
                )}
              </span>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrintInvoice}
                  disabled={!previewInvoice?.invoice_content}
                  data-testid="invoice-preview-print"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadInvoice}
                  disabled={!previewInvoice?.invoice_content}
                  data-testid="invoice-preview-download"
                >
                  <Upload className="w-4 h-4 mr-2 rotate-180" />
                  Download
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto border border-gray-200 rounded-lg bg-white">
            {previewLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e6007e] mx-auto"></div>
                  <p className="mt-3 text-sm text-gray-600 font-body">Loading invoice…</p>
                </div>
              </div>
            ) : previewInvoice?.invoice_content ? (
              <iframe
                title="Invoice preview"
                srcDoc={previewInvoice.invoice_content}
                className="w-full h-[65vh] border-0"
                data-testid="invoice-preview-iframe"
              />
            ) : (
              <div className="flex items-center justify-center h-64 text-sm text-gray-500 font-body">
                No invoice content available.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Invoice Edit Form Component

const InvoiceEditForm = ({ project, onUpdate }) => {
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [vatEnabled, setVatEnabled] = useState(project?.vat_enabled !== false); // Local state for VAT toggle
  const { token } = useAuth();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    invoice_due_days: project?.invoice_due_days || 30,
    external_invoice_reference: project?.external_invoice_reference || '',
    invoice_type: project?.invoice_type || 'generated',
    issue_date: new Date().toISOString().split('T')[0],
    invoice_number: project?.invoice_number || '',
    vat_scheme: 'cash', // cash, invoice, flat_rate
    default_vat_rate: 20,
    // Tier 2 Client Information
    client_name: project?.client_name || '',
    client_contact: '',
    client_email: project?.client_email || '',
    client_phone: '',
    client_address: '',
    // Tier 2 Payment Terms
    payment_terms: 'net_30',
    late_payment_rate: '8',
    payment_instructions: 'Payment can be made via the link provided. Multiple payment options available including installments and BNPL.',
    // Tier 2 Notes & Terms
    invoice_notes: '',
    terms_conditions: 'Payment terms: Net 30 days. Late payment interest may apply. Services provided subject to our standard terms and conditions.',
    // Tier 3 Template & Branding
    template: 'professional',
    color_scheme: 'paidd',
    custom_color: '#e6007e',
    header_text: 'INVOICE',
    footer_text: 'Thank you for your business',
    show_logo: true,
    show_payment_qr: false,
    show_late_fees: true,
    // Tier 3 VAT Automation
    auto_sweep_schedule: 'monthly',
    auto_sweep_minimum: 100,
    line_items: [
      {
        id: 1,
        description: project?.title || 'Service',
        quantity: 1,
        rate: project?.total_amount || 0,
        vat_rate: 20,
        net_amount: 0,
        vat_amount: 0,
        gross_amount: 0
      }
    ]
  });

  const [totals, setTotals] = useState({
    net: 0,
    vat: 0,
    gross: 0
  });

  // Calculate line item amounts - now accepts vatEnabled as parameter
  const calculateLineAmounts = (quantity, rate, vatRate, isVatEnabled = vatEnabled) => {
    const net = quantity * rate;
    // Use the passed vatEnabled parameter instead of state
    const vat = isVatEnabled ? net * (vatRate / 100) : 0;
    const gross = net + vat;
    return { net, vat, gross };
  };

  // Calculate totals from all line items
  const calculateTotals = (lineItems, useVatEnabled = vatEnabled) => {
    const totals = lineItems.reduce((acc, item) => {
      const amounts = calculateLineAmounts(item.quantity, item.rate, item.vat_rate, useVatEnabled);
      return {
        net: acc.net + amounts.net,
        vat: acc.vat + amounts.vat,
        gross: acc.gross + amounts.gross
      };
    }, { net: 0, vat: 0, gross: 0 });
    
    setTotals(totals);
    return totals;
  };

  // Update line item
  const updateLineItem = (index, field, value) => {
    const updatedItems = [...formData.line_items];
    updatedItems[index][field] = value;
    
    // Recalculate amounts for this line
    if (['quantity', 'rate', 'vat_rate'].includes(field)) {
      const amounts = calculateLineAmounts(
        updatedItems[index].quantity,
        updatedItems[index].rate,
        updatedItems[index].vat_rate,
        vatEnabled
      );
      updatedItems[index] = { ...updatedItems[index], ...amounts };
    }
    
    setFormData({ ...formData, line_items: updatedItems });
    calculateTotals(updatedItems);
  };

  // Add new line item
  const addLineItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      rate: 0,
      vat_rate: formData.default_vat_rate,
      net_amount: 0,
      vat_amount: 0,
      gross_amount: 0
    };
    
    const updatedItems = [...formData.line_items, newItem];
    setFormData({ ...formData, line_items: updatedItems });
    calculateTotals(updatedItems);
  };

  // Remove line item
  const removeLineItem = (index) => {
    if (formData.line_items.length === 1) {
      toast({
        title: "Cannot Remove",
        description: "Invoice must have at least one line item",
        variant: "destructive"
      });
      return;
    }
    
    const updatedItems = formData.line_items.filter((_, i) => i !== index);
    setFormData({ ...formData, line_items: updatedItems });
    calculateTotals(updatedItems);
  };

  useEffect(() => {
    fetchInvoiceData();
    // Only update local VAT state if it's different from project, to prevent overriding user changes
    if (project?.vat_enabled !== vatEnabled) {
      setVatEnabled(project?.vat_enabled !== false);
    }
  }, [project]);

  useEffect(() => {
    // Calculate initial totals
    calculateTotals(formData.line_items);
  }, []);

  const fetchInvoiceData = async () => {
    if (!project?.is_invoice_project) return;
    
    try {
      const response = await axios.get(`${API}/projects/${project.id}/invoice`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoiceData(response.data);
      
      // If invoice has existing line items, load them
      if (response.data.line_items && response.data.line_items.length > 0) {
        setFormData({
          ...formData,
          line_items: response.data.line_items,
          invoice_number: response.data.invoice_number || '',
          issue_date: response.data.issue_date || formData.issue_date
        });
      }
    } catch (error) {
      console.log('Invoice not yet generated or error fetching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Save invoice with complete Tier 1, 2, and 3 data
      await axios.put(`${API}/projects/${project.id}/invoice`, {
        // Basic invoice data (Tier 1)
        invoice_due_days: parseInt(formData.invoice_due_days),
        external_invoice_reference: formData.external_invoice_reference,
        invoice_type: formData.invoice_type,
        issue_date: formData.issue_date,
        invoice_number: formData.invoice_number,
        // VAT settings (Tier 1)
        vat_scheme: formData.vat_scheme,
        default_vat_rate: formData.default_vat_rate,
        line_items: formData.line_items,
        totals: totals,
        // Tier 2: Client Information
        client_name: formData.client_name,
        client_contact: formData.client_contact,
        client_email: formData.client_email,
        client_phone: formData.client_phone,
        client_address: formData.client_address,
        // Tier 2: Payment Terms
        payment_terms: formData.payment_terms,
        late_payment_rate: formData.late_payment_rate,
        payment_instructions: formData.payment_instructions,
        // Tier 2: Notes & Terms
        invoice_notes: formData.invoice_notes,
        terms_conditions: formData.terms_conditions,
        // Tier 3: Template & Branding
        template: formData.template,
        color_scheme: formData.color_scheme,
        custom_color: formData.custom_color,
        header_text: formData.header_text,
        footer_text: formData.footer_text,
        show_logo: formData.show_logo,
        show_payment_qr: formData.show_payment_qr,
        show_late_fees: formData.show_late_fees,
        // Tier 3: VAT Automation
        auto_sweep_schedule: formData.auto_sweep_schedule,
        auto_sweep_minimum: formData.auto_sweep_minimum
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Invoice Saved",
        description: "Complete invoice with templates, branding, and VAT automation saved successfully"
      });

      onUpdate();
      fetchInvoiceData(); // Refresh invoice data
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update invoice",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvoice = async () => {
    try {
      await axios.post(`${API}/projects/${project.id}/invoice/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Invoice Sent",
        description: "Invoice has been sent to client successfully"
      });

      onUpdate();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send invoice",
        variant: "destructive"
      });
    }
  };

  const handleMarkPaid = async () => {
    try {
      await axios.post(`${API}/projects/${project.id}/invoice/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Invoice Marked Paid",
        description: "Invoice has been marked as paid and project completed"
      });

      onUpdate();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to mark invoice as paid",
        variant: "destructive"
      });
    }
  };

  if (!project?.is_invoice_project && !project?.invoice_number) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">No invoice on this project yet</h3>
          <p className="text-gray-600 font-body">An invoice is auto-issued as soon as the first milestone is paid, or you can convert this project to invoice billing.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Invoice Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 font-heading">
            <FileText className="w-5 h-5" />
            <span>Invoice Details</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Invoice Number</Label>
              <Input
                value={formData.invoice_number}
                onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
                placeholder="INV-2025-001"
                className="font-body"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Issue Date</Label>
              <Input
                type="date"
                value={formData.issue_date}
                onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
                className="font-body"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Payment Due (Days)</Label>
              <Select value={formData.invoice_due_days.toString()} onValueChange={(value) => setFormData({...formData, invoice_due_days: parseInt(value)})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-gray-500 font-body text-sm">Status:</span>
              <Badge variant={project.invoice_paid_at ? "default" : "secondary"} className="ml-2">
                {project.invoice_paid_at ? "Paid" : "Pending"}
              </Badge>
            </div>
            <div>
              <span className="text-gray-500 font-body text-sm">Due Date:</span>
              <span className="font-medium font-body ml-2">
                {formData.issue_date ? new Date(new Date(formData.issue_date).getTime() + (formData.invoice_due_days * 24 * 60 * 60 * 1000)).toLocaleDateString('en-GB') : "Not set"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items Editor Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between font-heading">
            <div className="flex items-center space-x-2">
              <Edit3 className="w-5 h-5" />
              <span>Invoice Line Items</span>
            </div>
            <Button
              onClick={addLineItem}
              variant="outline"
              size="sm"
              className="font-body"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Line
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Header Row */}
            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 font-body">
              <div className="col-span-4">Description</div>
              <div className="col-span-1 text-center">Qty</div>
              <div className="col-span-2 text-right">Rate</div>
              <div className="col-span-1 text-center">VAT %</div>
              <div className="col-span-2 text-right">Net</div>
              <div className="col-span-1 text-right">VAT</div>
              <div className="col-span-1 text-center">Actions</div>
            </div>
            
            {/* Line Items */}
            {formData.line_items.map((item, index) => (
              <div key={item.id} className="grid grid-cols-12 gap-2 items-center p-3 bg-gray-50 rounded-lg">
                <div className="col-span-4">
                  <Input
                    value={item.description}
                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    placeholder="Service description"
                    className="font-body text-sm"
                  />
                </div>
                <div className="col-span-1">
                  <Input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                    className="font-body text-sm text-center"
                    min="1"
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    value={item.rate}
                    onChange={(e) => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)}
                    className="font-body text-sm text-right"
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>
                <div className="col-span-1">
                  <Select 
                    value={vatEnabled ? item.vat_rate.toString() : "0"} 
                    onValueChange={(value) => updateLineItem(index, 'vat_rate', parseInt(value))}
                    disabled={!vatEnabled}
                  >
                    <SelectTrigger className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      {vatEnabled && (
                        <>
                          <SelectItem value="5">5%</SelectItem>
                          <SelectItem value="20">20%</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 text-right">
                  <span className="text-sm font-medium font-body">£{item.net_amount?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="col-span-1 text-right">
                  <span className="text-sm font-medium text-gray-600 font-body">£{item.vat_amount?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="col-span-1 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLineItem(index)}
                    className="text-red-600 hover:text-red-900 h-8 w-8 p-0"
                    disabled={formData.line_items.length === 1}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
            
            {/* Totals Row */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="grid grid-cols-3 gap-4 text-right">
                <div className="bg-gray-100 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 font-body">Subtotal (Net)</p>
                  <p className="text-lg font-bold text-gray-900 font-heading">£{totals.net.toFixed(2)}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <p className="text-xs text-blue-600 font-body">VAT</p>
                  <p className="text-lg font-bold text-blue-900 font-heading">£{totals.vat.toFixed(2)}</p>
                </div>
                <div className="bg-[#e6007e] bg-opacity-10 p-3 rounded-lg">
                  <p className="text-xs text-[#e6007e] font-body">Total</p>
                  <p className="text-2xl font-bold text-[#e6007e] font-heading">£{totals.gross.toFixed(2)}</p>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-purple-900 font-body">VAT to set aside ({formData.vat_scheme})</span>
                  <span className="text-lg font-bold text-purple-900 font-heading">£{totals.vat.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Client Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 font-heading">
            <Users className="w-5 h-5" />
            <span>Client Information</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Client Name</Label>
              <Input
                value={formData.client_name || project.client_name}
                onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                placeholder="Client Business Name"
                className="font-body"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Contact Person</Label>
              <Input
                value={formData.client_contact || ''}
                onChange={(e) => setFormData({...formData, client_contact: e.target.value})}
                placeholder="Contact Name"
                className="font-body"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Email</Label>
              <Input
                type="email"
                value={formData.client_email || project.client_email}
                onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                placeholder="client@company.com"
                className="font-body"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Phone</Label>
              <Input
                value={formData.client_phone || ''}
                onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                placeholder="+44 20 1234 5678"
                className="font-body"
              />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700 font-body">Billing Address</Label>
            <Textarea
              value={formData.client_address || ''}
              onChange={(e) => setFormData({...formData, client_address: e.target.value})}
              placeholder="123 Business Street&#10;City, County&#10;Postcode, UK"
              className="font-body"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Payment Terms & Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 font-heading">
            <CreditCard className="w-5 h-5" />
            <span>Payment Terms & Instructions</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Payment Terms</Label>
              <Select value={formData.payment_terms || 'net_30'} onValueChange={(value) => setFormData({...formData, payment_terms: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="net_7">Net 7 days</SelectItem>
                  <SelectItem value="net_14">Net 14 days</SelectItem>
                  <SelectItem value="net_30">Net 30 days</SelectItem>
                  <SelectItem value="net_60">Net 60 days</SelectItem>
                  <SelectItem value="net_90">Net 90 days</SelectItem>
                  <SelectItem value="due_on_receipt">Due on receipt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Late Payment Interest</Label>
              <Select value={formData.late_payment_rate || '8'} onValueChange={(value) => setFormData({...formData, late_payment_rate: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% (No interest)</SelectItem>
                  <SelectItem value="8">8% (Bank of England + 8%)</SelectItem>
                  <SelectItem value="10">10% (Custom rate)</SelectItem>
                  <SelectItem value="12">12% (Higher rate)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700 font-body">Payment Instructions</Label>
            <Textarea
              value={formData.payment_instructions || 'Payment can be made via the link provided. Multiple payment options available including installments and BNPL.'}
              onChange={(e) => setFormData({...formData, payment_instructions: e.target.value})}
              placeholder="Custom payment instructions for your client..."
              className="font-body"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Custom Notes & Terms Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 font-heading">
            <FileText className="w-5 h-5" />
            <span>Notes & Terms</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-gray-700 font-body">Invoice Notes</Label>
            <Textarea
              value={formData.invoice_notes || ''}
              onChange={(e) => setFormData({...formData, invoice_notes: e.target.value})}
              placeholder="Additional notes or project details for this invoice..."
              className="font-body"
              rows={3}
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700 font-body">Terms & Conditions</Label>
            <Textarea
              value={formData.terms_conditions || 'Payment terms: Net 30 days. Late payment interest may apply. Services provided subject to our standard terms and conditions.'}
              onChange={(e) => setFormData({...formData, terms_conditions: e.target.value})}
              placeholder="Custom terms and conditions..."
              className="font-body"
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      {/* VAT Settings Card - Moved here between Notes & Terms and VAT Vault */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 font-heading">
            <PiggyBank className="w-5 h-5" />
            <span>VAT Settings</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* VAT Enable/Disable Toggle */}
          <div className="border rounded-lg p-4 bg-gradient-to-r from-green-50 to-blue-50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <PiggyBank className="w-4 h-4 text-green-600" />
                <Label className="font-semibold text-gray-900 font-heading">Enable VAT</Label>
              </div>
              <div className="flex items-center space-x-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={vatEnabled}
                    onChange={(e) => {
                      const newVatEnabled = e.target.checked;
                      
                      // Update local state immediately for responsive UI
                      setVatEnabled(newVatEnabled);
                      
                      // Update the default VAT rate based on VAT enabled status
                      const newDefaultRate = newVatEnabled ? (formData.default_vat_rate || 20) : 0;
                      
                      // Recalculate all line items immediately with correct VAT state
                      const updatedItems = formData.line_items.map(item => {
                        let itemVatRate;
                        if (newVatEnabled) {
                          // When enabling VAT, restore original rate or use default
                          itemVatRate = item.original_vat_rate || newDefaultRate;
                        } else {
                          // When disabling VAT, store original rate and set to 0
                          itemVatRate = 0;
                        }
                        
                        const amounts = calculateLineAmounts(item.quantity, item.rate, itemVatRate, newVatEnabled);
                        return { 
                          ...item, 
                          vat_rate: itemVatRate,
                          original_vat_rate: newVatEnabled ? (item.original_vat_rate || item.vat_rate || newDefaultRate) : (item.original_vat_rate || item.vat_rate || 20),
                          ...amounts 
                        };
                      });
                      
                      // Update form data with new items and default rate
                      setFormData({ ...formData, line_items: updatedItems, default_vat_rate: newDefaultRate });
                      
                      // Force recalculate totals immediately with correct VAT state
                      const newTotals = calculateTotals(updatedItems, newVatEnabled);
                      console.log('Recalculated totals after VAT toggle:', newTotals);
                      
                      // Call backend to update project VAT setting
                      axios.put(`${API}/projects/${project.id}`, {
                        vat_enabled: newVatEnabled
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      }).then((response) => {
                        console.log('VAT update response:', response.data);
                        toast({
                          title: "VAT Setting Updated",
                          description: `VAT has been ${newVatEnabled ? 'enabled' : 'disabled'} for this invoice`
                        });
                        // Don't call onUpdate() immediately to avoid state conflicts
                        // onUpdate(); 
                      }).catch((error) => {
                        console.error('VAT update error:', error);
                        // Revert local state on error
                        setVatEnabled(!newVatEnabled);
                        toast({
                          title: "Error",
                          description: "Failed to update VAT setting",
                          variant: "destructive"
                        });
                      });
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
                <span className="text-sm font-body">
                  {vatEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
            <div className="text-xs text-gray-600 font-body">
              {vatEnabled ? (
                <p>✓ VAT will be calculated and included in this invoice</p>
              ) : (
                <p>⚪ No VAT will be applied to this invoice</p>
              )}
            </div>
          </div>

          {/* VAT Configuration - Only show when VAT is enabled */}
          {vatEnabled && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-700 font-body">VAT Scheme</Label>
                <Select value={formData.vat_scheme} onValueChange={(value) => setFormData({...formData, vat_scheme: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash Accounting</SelectItem>
                    <SelectItem value="invoice">Invoice Accounting</SelectItem>
                    <SelectItem value="flat_rate">Flat Rate Scheme</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 font-body">Default VAT Rate (%)</Label>
                <Select 
                  value={vatEnabled ? formData.default_vat_rate.toString() : "0"} 
                  onValueChange={(value) => {
                    const newRate = parseInt(value);
                    setFormData({...formData, default_vat_rate: newRate});
                    
                    // Update all line items to use the new default rate if VAT is enabled
                    if (vatEnabled) {
                      const updatedItems = formData.line_items.map(item => {
                        const amounts = calculateLineAmounts(item.quantity, item.rate, newRate, vatEnabled);
                        return { ...item, vat_rate: newRate, original_vat_rate: newRate, ...amounts };
                      });
                      setFormData(prev => ({ ...prev, line_items: updatedItems }));
                      calculateTotals(updatedItems, vatEnabled);
                    }
                  }}
                  disabled={!vatEnabled}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% (VAT Disabled)</SelectItem>
                    {vatEnabled && (
                      <>
                        <SelectItem value="5">5% (Reduced rate)</SelectItem>
                        <SelectItem value="20">20% (Standard rate)</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {/* Current Project VAT Rate Display */}
          {vatEnabled && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-900 font-body">Current Project VAT Rate</p>
                  <p className="text-xs text-blue-700 font-body">
                    Rate being used in invoice line items
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-blue-900 font-heading">
                    {(() => {
                      // Get the most common non-zero VAT rate from line items
                      const rates = formData.line_items
                        .map(item => item.vat_rate)
                        .filter(rate => rate > 0);
                      
                      if (rates.length === 0) return "0%";
                      
                      // Find most common rate
                      const rateCounts = rates.reduce((acc, rate) => {
                        acc[rate] = (acc[rate] || 0) + 1;
                        return acc;
                      }, {});
                      
                      const mostCommonRate = Object.keys(rateCounts)
                        .reduce((a, b) => rateCounts[a] > rateCounts[b] ? a : b);
                      
                      return mostCommonRate + "%";
                    })()}
                  </p>
                  <p className="text-xs text-blue-600 font-body">
                    Effective Rate
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {!vatEnabled && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 font-body">Current Project VAT Rate</p>
                  <p className="text-xs text-gray-600 font-body">
                    VAT is disabled for this project
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-700 font-heading">0%</p>
                  <p className="text-xs text-gray-600 font-body">
                    No VAT Applied
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {vatEnabled && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-700 font-body">
                <strong>{formData.vat_scheme === 'cash' ? 'Cash Accounting' : formData.vat_scheme === 'invoice' ? 'Invoice Accounting' : 'Flat Rate Scheme'}:</strong> 
                {formData.vat_scheme === 'cash' ? ' VAT due when payments are received' : 
                 formData.vat_scheme === 'invoice' ? ' VAT due when invoices are issued' :
                 ' Fixed VAT rate based on business sector'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* VAT Vault Tier 2 - Monthly Sweep Suggestions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 font-heading">
            <PiggyBank className="w-5 h-5" />
            <span>VAT Vault - Smart Separation</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-semibold text-purple-900 font-heading">Monthly VAT Sweep</h4>
                <p className="text-sm text-purple-700 font-body">Suggested amount to transfer to your VAT savings account</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-purple-900 font-heading">£{totals.vat.toFixed(2)}</p>
                <p className="text-xs text-purple-600 font-body">This invoice</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const bankInstructions = `Transfer Details:\n\nAmount: £${totals.vat.toFixed(2)}\nTo: Your VAT Savings Account\nReference: VAT-${formData.invoice_number || new Date().getMonth() + 1}-2025\nDue: ${new Date(new Date().setDate(new Date().getDate() + 7)).toLocaleDateString('en-GB')}`;
                  navigator.clipboard.writeText(bankInstructions);
                  toast({
                    title: "Bank Instructions Copied",
                    description: "Paste into your banking app to set up the transfer"
                  });
                }}
                className="font-body"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Bank Instructions
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  toast({
                    title: "Direct Debit Setup",
                    description: "Contact your bank to set up monthly VAT transfers. This feature will be automated soon."
                  });
                }}
                className="font-body"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Setup Auto-Transfer
              </Button>
            </div>
            
            <div className="text-xs text-purple-600 font-body">
              💡 Set aside VAT as you earn to avoid cash flow issues when payments are due to HMRC
            </div>
          </div>
        </CardContent>
      </Card>

      {/* VAT Vault Tier 3 - Automated Separation Rules */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 font-heading">
            <Zap className="w-5 h-5" />
            <span>VAT Vault - Automated Rules (Tier 3)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-semibold font-heading">Smart VAT Automation</h4>
                <p className="text-sm opacity-90 font-body">Set rules for automatic VAT separation</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold font-heading">£{totals.vat.toFixed(2)}</p>
                <p className="text-xs opacity-80 font-body">Ready to automate</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-white font-body">Separation Schedule</Label>
                <Select value={formData.auto_sweep_schedule || 'monthly'} onValueChange={(value) => setFormData({...formData, auto_sweep_schedule: value})}>
                  <SelectTrigger className="bg-white text-black">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">On each invoice payment</SelectItem>
                    <SelectItem value="weekly">Weekly sweeps</SelectItem>
                    <SelectItem value="monthly">Monthly (recommended)</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white font-body">Minimum Amount</Label>
                <Input
                  type="number"
                  value={formData.auto_sweep_minimum || 100}
                  onChange={(e) => setFormData({...formData, auto_sweep_minimum: parseFloat(e.target.value)})}
                  className="bg-white text-black font-body"
                  placeholder="100"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                className="bg-white text-purple-600 hover:bg-gray-100 font-body text-xs"
                onClick={() => {
                  toast({
                    title: "BNPL VAT Rules",
                    description: "VAT allocated proportionally across installment payments based on scheme settings"
                  });
                }}
              >
                <Clock className="w-3 h-3 mr-1" />
                BNPL Rules
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="bg-white text-purple-600 hover:bg-gray-100 font-body text-xs"
                onClick={() => {
                  const reconciliation = `VAT Reconciliation Report\n\nInvoice: ${formData.invoice_number}\nNet: £${totals.net.toFixed(2)}\nVAT: £${totals.vat.toFixed(2)}\nScheme: ${formData.vat_scheme}\nSweep needed: £${totals.vat.toFixed(2)}`;
                  navigator.clipboard.writeText(reconciliation);
                  toast({
                    title: "Reconciliation Report",
                    description: "VAT reconciliation data copied to clipboard"
                  });
                }}
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Reconcile
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="bg-white text-purple-600 hover:bg-gray-100 font-body text-xs"
                onClick={() => {
                  toast({
                    title: "Partner Integration",
                    description: "Direct Debit automation setup - connects with regulated banking partner for automated sweeps"
                  });
                }}
              >
                <Shield className="w-3 h-3 mr-1" />
                Setup Partner
              </Button>
            </div>
            
            <div className="mt-4 text-xs opacity-80 font-body">
              🔒 Automated via regulated banking partner • Your accounts only • No Paify custody
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Templates & Branding (Tier 3) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 font-heading">
            <Settings className="w-5 h-5" />
            <span>Template & Branding</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Invoice Template</Label>
              <Select value={formData.template || 'professional'} onValueChange={(value) => setFormData({...formData, template: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional (Default)</SelectItem>
                  <SelectItem value="modern">Modern Minimal</SelectItem>
                  <SelectItem value="classic">Classic Business</SelectItem>
                  <SelectItem value="creative">Creative Agency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Color Scheme</Label>
              <Select value={formData.color_scheme || 'paidd'} onValueChange={(value) => setFormData({...formData, color_scheme: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paidd">Paify Pink (#e6007e)</SelectItem>
                  <SelectItem value="blue">Professional Blue</SelectItem>
                  <SelectItem value="green">Business Green</SelectItem>
                  <SelectItem value="black">Minimal Black</SelectItem>
                  <SelectItem value="custom">Custom Color</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {formData.color_scheme === 'custom' && (
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Custom Color (Hex)</Label>
              <Input
                value={formData.custom_color || '#e6007e'}
                onChange={(e) => setFormData({...formData, custom_color: e.target.value})}
                placeholder="#e6007e"
                className="font-body"
              />
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Header Text</Label>
              <Input
                value={formData.header_text || 'INVOICE'}
                onChange={(e) => setFormData({...formData, header_text: e.target.value})}
                placeholder="INVOICE"
                className="font-body"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 font-body">Footer Text</Label>
              <Input
                value={formData.footer_text || 'Thank you for your business'}
                onChange={(e) => setFormData({...formData, footer_text: e.target.value})}
                placeholder="Thank you for your business"
                className="font-body"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Checkbox 
                checked={formData.show_logo || true}
                onCheckedChange={(checked) => setFormData({...formData, show_logo: checked})}
              />
              <span className="text-sm font-body">Show company logo</span>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                checked={formData.show_payment_qr || false}
                onCheckedChange={(checked) => setFormData({...formData, show_payment_qr: checked})}
              />
              <span className="text-sm font-body">Include payment QR code</span>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                checked={formData.show_late_fees || true}
                onCheckedChange={(checked) => setFormData({...formData, show_late_fees: checked})}
              />
              <span className="text-sm font-body">Show late payment terms</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card>
        <CardContent className="p-6">
          <div className="flex space-x-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-black hover:bg-gray-800 text-white font-body"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Invoice"}
            </Button>
            
            {invoiceData && (
              <Button
                onClick={() => setShowPreview(true)}
                variant="outline"
                className="font-body"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Invoice
              </Button>
            )}
            
            {project.invoice_number && !project.invoice_paid_at && (
              <>
                <Button
                  onClick={handleSendInvoice}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-body"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send to Client
                </Button>
                
                <Button
                  onClick={handleMarkPaid}
                  className="bg-green-600 hover:bg-green-700 text-white font-body"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark as Paid
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Legacy Settings (kept for backward compatibility) */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Invoice Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="external_invoice_reference" className="text-sm font-medium text-gray-700 font-body">
                Reference
              </Label>
              <Input
                id="external_invoice_reference"
                value={formData.external_invoice_reference}
                onChange={(e) => setFormData({...formData, external_invoice_reference: e.target.value})}
                placeholder="INV-2024-001"
                className="font-body"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Preview Modal */}
      {showPreview && invoiceData && (
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                Invoice Preview - {invoiceData.invoice_number}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              <div className="bg-white rounded-lg border shadow-sm">
                <div 
                  dangerouslySetInnerHTML={{ __html: invoiceData.invoice_content }}
                  className="invoice-preview"
                />
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600 font-body">
                  <p>Invoice will be sent to: <strong>{project.client_email}</strong></p>
                  <p>Client will receive payment link to choose payment structure</p>
                </div>
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowPreview(false)}
                    className="font-body"
                  >
                    Close Preview
                  </Button>
                  <Button
                    onClick={() => {
                      handleSendInvoice();
                      setShowPreview(false);
                    }}
                    className="bg-[#e6007e] hover:bg-[#c4006b] text-white font-body"
                  >
                    Send to Client
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Checkout Page Component  

// CheckoutPage extracted to ./components/


export { InvoicesPage, InvoiceEditForm };
